// In-memory stale-while-revalidate cache for getMarketplaceHome() responses.
//
// Purpose: make tab-flicks (Explore <-> Bookings <-> Account) feel instant.
// On revisit, we read the cached payload synchronously, render immediately,
// and kick off a fresh fetch in the background. If the fresh response differs,
// state is updated silently.
//
// Scope: module-level (survives route changes within a session, cleared on
// app restart). Deliberately NOT in localStorage - redux-persist was just
// narrowed to avoid bloating the boot hydrate, and this cache is only useful
// while the tab is open.
//
// Freshness: 5 minutes. Short enough that a stale cache doesn't show deeply
// out-of-date salon lists, long enough that normal in-session navigation
// always hits cache.
//
// Location sensitivity: a cache entry is tied to the lat/lng it was fetched
// with. If the user's resolved location moves materially (>0.05 deg, ~5 km)
// we treat the cache as a miss so distance-sorted results stay accurate.

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const LOCATION_THRESHOLD_DEG = 0.05; // ~5 km — matches NewHomePage's refetch threshold

let cached = null; // { data, timestamp, latitude, longitude }

/**
 * Return the cached homeData if it's fresh AND was fetched close to `location`.
 * Returns null on cache miss. Never throws.
 */
export const getMarketplaceHomeCache = (location) => {
  if (!cached) return null;
  if (Date.now() - cached.timestamp > CACHE_TTL_MS) return null;

  if (
    location &&
    typeof location.latitude === 'number' &&
    typeof cached.latitude === 'number'
  ) {
    const latDelta = Math.abs(location.latitude - cached.latitude);
    const lngDelta = Math.abs(location.longitude - cached.longitude);
    if (latDelta > LOCATION_THRESHOLD_DEG || lngDelta > LOCATION_THRESHOLD_DEG) {
      return null;
    }
  }

  return cached.data;
};

/**
 * Store a homeData payload along with the location it was fetched with.
 * Overwrites any previous entry.
 */
export const setMarketplaceHomeCache = (data, location) => {
  if (!data) return;
  cached = {
    data,
    timestamp: Date.now(),
    latitude: location?.latitude,
    longitude: location?.longitude,
  };
};

/** Clear the cache. Exported for completeness (e.g. on logout). */
export const clearMarketplaceHomeCache = () => {
  cached = null;
};
